import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { providerRegistryApi } from "../../api/providerRegistryApi";
import type { ProviderRegistry } from "../../types/providerRegistry.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import i18n from "../../i18n";

interface ProviderRegistryState {
  items: ProviderRegistry[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: ProviderRegistryState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const providerRegistrySlice = createSlice({
  name: "providerRegistry",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setProviderRegistries: (
      state,
      action: PayloadAction<ProviderRegistry[]>,
    ) => {
      state.items = action.payload;
    },
    appendProviderRegistries: (
      state,
      action: PayloadAction<ProviderRegistry[]>,
    ) => {
      state.items = [...state.items, ...action.payload];
    },
    addSingleProviderRegistryLocally: (
      state,
      action: PayloadAction<ProviderRegistry>,
    ) => {
      state.items = [action.payload, ...state.items];
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetProviderRegistryList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },
    updateSingleProviderRegistry: (
      state,
      action: PayloadAction<
        Partial<ProviderRegistry> & { providerCode: string }
      >,
    ) => {
      const index = state.items.findIndex(
        (item) => item.providerCode === action.payload.providerCode,
      );
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeSingleProviderRegistry: (state, action: PayloadAction<string>) => {
      // providerCode se remove kar rahe hain
      state.items = state.items.filter(
        (item) => item.providerCode !== action.payload,
      );
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setProviderRegistries,
  appendProviderRegistries,
  addSingleProviderRegistryLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetProviderRegistryList,
  updateSingleProviderRegistry,
  removeSingleProviderRegistry,
} = providerRegistrySlice.actions;

// Async Thunks

export const fetchProviderRegistries = (
  payload: {
    pageSize?: number;
    pageIndex?: number;
    orderBy?: string;
    sortDecending?: boolean;
    groupIds?: string[];
    ids?: string[];
    categories?: string[];
    /** Provider-management screens need to see providers that don't have
     * any configurable fields yet — the default filter (used by the
     * category-picker dropdowns) hides those. */
    includeEmpty?: boolean;
  } = {},
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().providerRegistry;
    const incomingPageIndex = payload.hasOwnProperty("pageIndex")
      ? payload.pageIndex!
      : state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "string",
        sortDecending: payload.sortDecending ?? true,
        groupIds: payload.groupIds || [],
        ids: payload.ids || [],
        categories: payload.categories || [
          "Storage",
          "Search",
          "Documents",
          "Postgres",
        ],
      };

      const response =
        await providerRegistryApi.searchProviderRegistries(requestBody);
      const rawData = response.data || [];
      const totalCount = response.count ?? response.totalCount ?? 0;

      // ✅ Filter: Sirf unhi providers ko rakho jinme settingDefinitions null/undefined na ho aur usme items ho
      const fetchedData = payload.includeEmpty
        ? rawData
        : rawData.filter(
            (provider: any) =>
              Array.isArray(provider.settingDefinitions) &&
              provider.settingDefinitions.length > 0,
          );

      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      dispatch(
        setHasMore(
          currentLoadedCount < totalCount &&
            rawData.length >= (payload.pageSize || 10),
        ),
      );

      if (incomingPageIndex === 0) {
        dispatch(setProviderRegistries(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendProviderRegistries(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Provider Registries error:", error);
      toast.error(
        i18n.t("common:slices.providerRegistry.fetchError", {
          defaultValue: "Failed to fetch provider registries",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};
export const addProviderRegistry = (data: Partial<ProviderRegistry>) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));
      const res = await providerRegistryApi.createproviderRegistry(data);
      const createdItem = res?.data || res;

      if (createdItem && (createdItem.providerCode || createdItem.id)) {
        dispatch(addSingleProviderRegistryLocally(createdItem));
      } else {
        dispatch(fetchProviderRegistries({ pageIndex: 0 }));
      }

      toast.success(
        i18n.t("common:slices.providerRegistry.addSuccess", {
          defaultValue: "Provider registry created successfully",
        }),
      );
      return createdItem;
    } catch (error) {
      console.error("Create Provider Registry error:", error);
      toast.error(
        i18n.t("common:slices.providerRegistry.addError", {
          defaultValue: "Failed to create provider registry",
        }),
      );
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editProviderRegistry = (
  data: Partial<ProviderRegistry> & { providerCode: string },
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));
      const res = await providerRegistryApi.updateproviderRegistry(data);
      const updatedItem = res?.data || data;

      dispatch(updateSingleProviderRegistry(updatedItem));

      toast.success(
        i18n.t("common:slices.providerRegistry.editSuccess", {
          defaultValue: "Provider registry updated successfully",
        }),
      );
      return updatedItem;
    } catch (error) {
      console.error("Edit Provider Registry error:", error);
      toast.error(
        i18n.t("common:slices.providerRegistry.editError", {
          defaultValue: "Failed to update provider registry",
        }),
      );
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const setProviderRegistryStatus = (
  id: string,
  status: number,
  providerCode: string,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));
      await providerRegistryApi.setProviderRegistryStatus(id, status);
      dispatch(updateSingleProviderRegistry({ providerCode, status }));

      toast.success(
        i18n.t("common:slices.providerRegistry.statusSuccess", {
          defaultValue: "Provider status updated successfully",
        }),
      );
    } catch (error) {
      console.error("Set Provider Registry Status error:", error);
      toast.error(
        i18n.t("common:slices.providerRegistry.statusError", {
          defaultValue: "Failed to update provider status",
        }),
      );
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const removeProviderRegistry = (idOrCode: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      // Optimistic Remove
      dispatch(removeSingleProviderRegistry(idOrCode));

      await providerRegistryApi.removeproviderRegistry(idOrCode);

      toast.success(
        i18n.t("common:slices.providerRegistry.removeSuccess", {
          defaultValue: "Provider registry removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Provider Registry error:", error);
      toast.error(
        i18n.t("common:slices.providerRegistry.removeError", {
          defaultValue: "Failed to remove provider registry",
        }),
      );
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default providerRegistrySlice.reducer;
